/**
 * Application Domain Definition (domain-def@4) — the product artifact.
 *
 * `Application` (domain-model.md §3.10) — the cover letter is authored per
 * application as FREE prose with a provenance map to the ledger; the Cv is
 * tailored via a `CvTailoring` VIEW (include/order/emphasis), never a CV mutation
 * (law C6). An Application is ALWAYS revisitable — its content IS the
 * event-sourced state (law C8).
 *
 * Re-derivation:
 *  - Slimmed (critique 7): NO `motivationsschreiben`, NO reserved-inactive
 *    smart-link kinds; document kinds grow additively when a document ships.
 *  - NEW `application.RawAssembled` — the raw authoring bundle (selected claims,
 *    angle, constraints, pinned context/cv refs), the preselect output persisted
 *    for reproducibility (llm-workflows §4.6).
 *  - `application.CoverLetterAuthored` carries FREE prose + a `ProvenanceMap` +
 *    `PresentationMeta` (the 5-paragraph `{text, claimRefs}[]` straitjacket is
 *    retired; per-passage provenance survives via the map — llm-workflows §3a).
 *  - The reviewable edit loop uses the shared `ProposedEdit` primitive.
 *
 * Stream: `Agg::career::{userId}::Application::{applicationId}`.
 */

import type { DomainDefinitionJSON } from "./schema"

const applicationDomain = {
  schemaVersion: "domain-def@4",
  name: "Application",
  version: "1.0.0",
  description:
    "Application — per-application free-prose cover letter (provenance map) + CvTailoring view over the durable Cv, rendered and packaged; always revisitable",

  types: {
    // ─── Raw authoring bundle (the preselect output — RawAssembled) ─

    /**
     * The RAW bundle for authoring: the ANGLE (the "what to say" decision), the
     * lead claims, curated attachments + smart links, and the pinned context / cv
     * refs (URN + version). Persisted so presentation is reproducible / re-runnable
     * (new locale, new tone) without re-extraction.
     */
    ApplicationRawBundle: {
      "+": "reject",
      /** The selected angle — the "what to say" decision. */
      angle: "string",
      /** Lead claims selected for the letter (URN refs into the confirmed ledger). */
      leadClaimRefs: "Urn[]",
      /** Pinned context refs (candidate / search / opportunity), URN + version. */
      contextRefs: "ContextRef[]",
      /** The Cv version this bundle was assembled against. */
      cvVersion: "number.integer >= 1",
      /** Curated enclosures — AI-preselected, user-toggled. */
      attachments: "ApplicationAttachment[]",
      /** External smart-links — AI-preselected, user-toggled. */
      smartLinks: "SmartLink[]",
      /** The derived address register (du|Sie), READ off `opportunity.Verified.register` and PINNED
       *  here at RawAssembled time (slice-5 additive: register is a raw-bundle datum persisted for
       *  reproducibility — domain-model §3.10 + the preselect ruling — so a re-presentation reads it
       *  from the bundle, never re-reaching the opportunity). Never model-authored. */
      "register?": "AddressRegister",
      /** Hard constraints for authoring (length budget, salutation facts). */
      "constraints?": "string",
    },

    // ─── Cover letter (free prose + provenance map, §3a) ─────────

    /**
     * The presented cover letter — freely-authored German markdown `body` (the
     * paragraph straitjacket is retired) with its provenance side-channel map and
     * authorship provenance. DIN 5008 letterhead is system-composed, never authored.
     */
    CoverLetter: {
      "+": "reject",
      "salutation?": "string",
      /** The letter as freely-authored markdown. */
      body: "Markdown",
      /** Per-passage citation map (anchor∈body → confirmed-claim URNs). */
      provenance: "ProvenanceMap",
      "subjectLine?": "string",
      "enclosureLine?": "string",
      "closing?": "string",
      meta: "PresentationMeta",
    },

    /**
     * Kurzprofil — a per-opportunity short professional profile (3–5 sentences),
     * authored per application (NOT the durable, opportunity-agnostic
     * `Cv.basics.summary`). Same RAW → PRESENTATION discipline as the cover letter:
     * freely-authored German markdown `body` + a provenance map (anchors → confirmed
     * claims) + authorship `meta`.
     */
    Kurzprofil: {
      "+": "reject",
      body: "Markdown",
      provenance: "ProvenanceMap",
      meta: "PresentationMeta",
    },

    // ─── CvTailoring (a view over the durable Cv) ────────────────

    CvTailoringSection: {
      "+": "reject",
      /** The JSON-Resume section (shared CvSectionName vocabulary). */
      section: "CvSectionName",
      include: "boolean",
      "order?": "number >= 0",
      "emphasis?": "string",
      /** Optional per-section rewrite — never mutates the base Cv */
      "rewrite?": "string",
    },

    CvTailoring: {
      "+": "reject",
      cvRef: "Urn",
      cvVersion: "number.integer >= 1",
      sections: "CvTailoringSection[]",
    },

    // ─── Rendering & packaging ───────────────────────────────────

    RenderTarget: "'app_template' | 'markdown_export'",

    GateResult: {
      "+": "reject",
      pass: "boolean",
      "notes?": "string",
    },

    /** Six quality gates: evidence / role-fit / motivation / clarity / anti-slop / reader-test */
    QualityGates: {
      "+": "reject",
      evidence: "GateResult",
      roleFit: "GateResult",
      motivation: "GateResult",
      clarity: "GateResult",
      antiSlop: "GateResult",
      readerTest: "GateResult",
    },

    /**
     * The application lifecycle. `sent` is NEVER auto-set by the tool — only the
     * user marks that they submitted externally (the gate holds). Transitions are
     * governed by an explicit state machine; illegal transitions are rejected.
     */
    ApplicationStatus:
      "'draft' | 'in_review' | 'changes_requested' | 'approved' | 'sent'",

    RenderedAsset: {
      "+": "reject",
      assetKind: "'cover_letter' | 'cv' | 'attachment'",
      renderTarget: "RenderTarget",
      "objectRef?": "ObjectRef | null",
    },

    /** The digital application as a downloadable ZIP (render-on-demand; export == view). */
    ApplicationPackage: {
      "+": "reject",
      id: "Urn",
      applicationRef: "Urn",
      format: "'zip'",
      renderTarget: "RenderTarget",
      contents: "RenderedAsset[]",
      /** Enclosed Asset URNs ("Anlagen") */
      enclosures: "Urn[]",
      "objectRef?": "ObjectRef | null",
      createdAt: "Timestamp",
    },

    // ─── Curated Anlagen + Smart-Links ───────────────────────────

    /** A curated enclosure ("Anlage") — AI-preselected with a rationale; user-toggled. */
    ApplicationAttachment: {
      "+": "reject",
      assetRef: "Urn",
      include: "boolean",
      "order?": "number >= 0",
      /** Why the AI chose it — drives low-effort review */
      "rationale?": "string",
    },

    /**
     * Smart-Link kind — EXTERNAL links only (the reserved-inactive
     * `career_studio_profile` self-link is dropped; it re-enters additively when real).
     */
    SmartLinkKind: "'profile_site' | 'portfolio' | 'reference' | 'other'",

    /** An external link the AI preselects for a placement; the user toggles `include`. */
    SmartLink: {
      "+": "reject",
      kind: "SmartLinkKind",
      url: "string",
      "label?": "string",
      include: "boolean",
      placement: "'letterhead' | 'cv_basics' | 'signature'",
      "rationale?": "string",
    },

    /** Which generation pass produced an annotation (transparency). */
    GenerationPassKind: "'preselect' | 'author' | 'tailor' | 'critique' | 'revise' | 'email'",

    /** A single gate-tagged critic annotation (one shared vocabulary the reviewer consumes).
     *  `id` is server-generated (stable per annotation — the `AnnotationResolved` join key);
     *  `anchor` is the server-derived TextAnchor for a verbatim `span` (absent when the span
     *  does not resolve verbatim in the letter body — `span` stays as the legacy fallback). */
    GenerationAnnotation: {
      "+": "reject",
      "id?": "string",
      gate: "'evidence' | 'roleFit' | 'motivation' | 'clarity' | 'antiSlop' | 'readerTest'",
      severity: "'blocker' | 'major' | 'minor' | 'nit' | 'positive'",
      kind: "'slop-flag' | 'evidence-gap' | 'claim-doubt' | 'factual-correction' | 'rewrite-request' | 'praise'",
      "span?": "string",
      "anchor?": "TextAnchor",
      comment: "NonEmptyString",
      "suggestedRewrite?": "string",
      "resolved?": "boolean",
    },

    /** One captured pass of the multi-pass pipeline; `runStepIndex` indexes the Run.steps[]. */
    GenerationPass: {
      "+": "reject",
      kind: "GenerationPassKind",
      iteration: "number.integer >= 0",
      "temperature?": "number >= 0",
      annotations: "GenerationAnnotation[]",
      "runStepIndex?": "number.integer >= 0",
    },

    /** The user-facing summary of the creative → verify → converge generation. */
    GenerationTrace: {
      "+": "reject",
      runRef: "Urn",
      passes: "GenerationPass[]",
      iterations: "number.integer >= 0",
      converged: "boolean",
      "stopReason?": "string",
    },

    Application: {
      "+": "reject",
      id: "Urn",
      owner: "UserId",
      opportunityRef: "Urn",
      /** Typed context snapshots it was built from (provenance + reproducibility). */
      contextSnapshots: "ContextSnapshot[]",
      /** The raw authoring bundle (preselect output), pinned for reproducibility. */
      "rawBundle?": "ApplicationRawBundle | null",
      /** The Cv version this application was built from */
      cvVersion: "number.integer >= 1",
      /** The single main letter — the Anschreiben (free prose + provenance map). */
      coverLetter: "CoverLetter",
      /** A SHORT e-mail cover note derived from the main letter — NOT a second long letter. */
      "emailNote?": "string | null",
      /** Per-opportunity Kurzprofil (short professional profile) — model-authored with a
       *  provenance map; distinct from the durable, opportunity-agnostic Cv.basics.summary. */
      "kurzprofil?": "Kurzprofil | null",
      cvTailoring: "CvTailoring",
      /** Curated enclosures ("Anlagen") — AI-preselected, user-toggled. */
      "attachments?": "ApplicationAttachment[]",
      /** External smart-links — AI-preselected, user-toggled. */
      "smartLinks?": "SmartLink[]",
      /** The creative → verify → converge trace; null when generated single-pass. */
      "generationTrace?": "GenerationTrace | null",
      /** Reviewable edits over the durable content — landed only on approval. */
      "proposedEdits?": "ProposedEdit[]",
      /** Selected DIN-A4 template-family id; defaults to `app_template`. */
      "templateId?": "string",
      renderTarget: "RenderTarget",
      qualityGates: "QualityGates",
      status: "ApplicationStatus",
      createdAt: "Timestamp",
      updatedAt: "Timestamp",
    },

    // ─── Event payloads ──────────────────────────────────────────

    /** Created against an opportunity; the initial context/cv refs are pinned. */
    ApplicationDraftedPayload: {
      "+": "reject",
      owner: "UserId",
      application: "Application",
    },

    /** The raw authoring bundle (preselect output) was assembled + persisted. */
    ApplicationRawAssembledPayload: {
      "+": "reject",
      owner: "UserId",
      applicationId: "Urn",
      raw: "ApplicationRawBundle",
    },

    /** The presented letter (free prose + provenance map + meta), plus the optional
     *  short email note and the convergence trace ref. */
    CoverLetterAuthoredPayload: {
      "+": "reject",
      owner: "UserId",
      applicationId: "Urn",
      coverLetter: "CoverLetter",
      "emailNote?": "string | null",
      /** The convergence trace this letter converged through. */
      "traceRef?": "Urn",
      "prov?": "ProvenanceBundle",
    },

    CvTailoredPayload: {
      "+": "reject",
      owner: "UserId",
      applicationId: "Urn",
      cvTailoring: "CvTailoring",
    },

    /** The per-opportunity Kurzprofil was (re)authored — presented prose + provenance map + meta
     *  (same RAW → PRESENTATION discipline as CoverLetterAuthored). */
    KurzprofilAuthoredPayload: {
      "+": "reject",
      owner: "UserId",
      applicationId: "Urn",
      kurzprofil: "Kurzprofil",
      /** The convergence trace this Kurzprofil converged through. */
      "traceRef?": "Urn",
      "prov?": "ProvenanceBundle",
    },

    QualityGatesEvaluatedPayload: {
      "+": "reject",
      owner: "UserId",
      applicationId: "Urn",
      qualityGates: "QualityGates",
      /** Annotations from the critique pass. */
      "annotations?": "GenerationAnnotation[]",
      /** The full convergence trace (passes + iterations + converged + stopReason) — the durable home
       *  for `Application.generationTrace` (slice 5 additive: no prior event carried the whole trace,
       *  only `CoverLetterAuthored.traceRef` — a URN pointer). Additive-optional. */
      "trace?": "GenerationTrace",
    },

    ApplicationStatusChangedPayload: {
      "+": "reject",
      owner: "UserId",
      applicationId: "Urn",
      from: "ApplicationStatus",
      to: "ApplicationStatus",
    },

    ApplicationPackagedPayload: {
      "+": "reject",
      owner: "UserId",
      applicationId: "Urn",
      "package": "ApplicationPackage",
    },

    ApplicationAttachmentsUpdatedPayload: {
      "+": "reject",
      owner: "UserId",
      applicationId: "Urn",
      attachments: "ApplicationAttachment[]",
    },

    ApplicationSmartLinksUpdatedPayload: {
      "+": "reject",
      owner: "UserId",
      applicationId: "Urn",
      smartLinks: "SmartLink[]",
    },

    /** A reviewable edit was staged (status `proposed`) — nothing has landed yet. */
    ApplicationEditProposedPayload: {
      "+": "reject",
      owner: "UserId",
      applicationId: "Urn",
      edit: "ProposedEdit",
    },

    /** A proposed edit was approved — `after` lands on the durable content. */
    ApplicationEditApprovedPayload: {
      "+": "reject",
      owner: "UserId",
      applicationId: "Urn",
      editId: "Urn",
      resolvedAt: "Timestamp",
    },

    /** A proposed edit was rejected — the content is untouched. */
    ApplicationEditRejectedPayload: {
      "+": "reject",
      owner: "UserId",
      applicationId: "Urn",
      editId: "Urn",
      resolvedAt: "Timestamp",
    },

    /** A quality-gate annotation was resolved by the user — applied (the suggested rewrite was
     *  taken through the edit loop) or dismissed. The fold flips `resolved` on the matching
     *  annotation in the generation trace; an unknown `annotationId` is tolerated (no-op). */
    ApplicationAnnotationResolvedPayload: {
      "+": "reject",
      owner: "UserId",
      applicationId: "Urn",
      annotationId: "string > 0",
      resolution: "'applied' | 'dismissed'",
      at: "Timestamp",
    },
  },

  resources: {
    application: {
      description: "A single application — the single source of truth, always revisitable",
      type: "Application",
      urnPattern: "urn:career:application:{applicationId}",
      fetch: { method: "GET", urlTemplate: "/api/v1/users/{userId}/applications/{applicationId}" },
      authz: { visibility: "user-owned", scopes: ["application:read"] },
    },
    applicationPackage: {
      description: "The downloadable ZIP package for an application",
      type: "ApplicationPackage",
      urnPattern: "urn:career:package:{applicationId}",
      fetch: { method: "GET", urlTemplate: "/api/v1/users/{userId}/applications/{applicationId}/package" },
      authz: { visibility: "user-owned", scopes: ["application:read"] },
    },
  },

  eventStream: {
    streamPattern: "Agg::career::{userId}::Application::{applicationId}",
    events: {
      "application.Drafted": {
        description: "An application was drafted against an opportunity; context/cv refs pinned",
        type: "ApplicationDraftedPayload",
      },
      "application.RawAssembled": {
        description: "The raw authoring bundle (selected claims, angle, constraints) was assembled + persisted",
        type: "ApplicationRawAssembledPayload",
      },
      "application.CoverLetterAuthored": {
        description: "The cover letter was authored as free prose with a provenance map + PresentationMeta",
        type: "CoverLetterAuthoredPayload",
      },
      "application.CvTailored": {
        description: "A CvTailoring view was applied (base Cv unchanged)",
        type: "CvTailoredPayload",
      },
      "application.KurzprofilAuthored": {
        description: "The per-opportunity Kurzprofil was authored as free prose with a provenance map + PresentationMeta",
        type: "KurzprofilAuthoredPayload",
      },
      "application.QualityGatesEvaluated": {
        description: "Quality gates (six) were evaluated + the critique pass annotations recorded",
        type: "QualityGatesEvaluatedPayload",
      },
      "application.StatusChanged": {
        description: "Application status changed (draft → in_review → approved → sent; + changes_requested); never sent by the tool",
        type: "ApplicationStatusChangedPayload",
      },
      "application.Packaged": {
        description: "The application was rendered and packaged as a downloadable ZIP (render-on-demand)",
        type: "ApplicationPackagedPayload",
      },
      "application.AttachmentsUpdated": {
        description: "The user adjusted the AI-preselected enclosures (Anlagen)",
        type: "ApplicationAttachmentsUpdatedPayload",
      },
      "application.SmartLinksUpdated": {
        description: "The user adjusted the AI-preselected smart-links",
        type: "ApplicationSmartLinksUpdatedPayload",
      },
      "application.EditProposed": {
        description: "A reviewable edit was staged (propose) — nothing landed yet",
        type: "ApplicationEditProposedPayload",
      },
      "application.EditApproved": {
        description: "A proposed edit was approved — its `after` landed on the durable content",
        type: "ApplicationEditApprovedPayload",
      },
      "application.EditRejected": {
        description: "A proposed edit was rejected — the content is untouched",
        type: "ApplicationEditRejectedPayload",
      },
      "application.AnnotationResolved": {
        description: "A quality-gate annotation was resolved by the user (applied or dismissed)",
        type: "ApplicationAnnotationResolvedPayload",
      },
    },
  },

  authz: {
    scopes: {
      "application:read": { description: "Read the owner's applications and packages" },
      "application:write": { description: "Author, tailor, render, and package applications" },
    },
    policies: {
      "owner-only": {
        kind: "owner",
        description: "Applications are owner-scoped to the calling Subject (law C1)",
      },
    },
    defaults: {
      unauthenticatedAccess: "deny",
      missingRolesBehavior: "deny",
    },
  },
} as const satisfies DomainDefinitionJSON

export default applicationDomain
